import { EMBROIDERY_TYPES, EmbroideryTypeKey } from '../combination-rules/types'
import { EMBROIDERY_VERSION_V2 } from '../embroideryVersion'
import { EmbroideryV2ItemCustomProperties } from '../types'

/** Prices in cents. */
export type EmbroideryPrice = { effectivePrice: number; fullPrice: number }

/** The minimal embroidery line shared by the cart (`CartItem`) and order history. */
export type EmbroideryLine = {
  key: string
  properties: EmbroideryV2ItemCustomProperties
  price: EmbroideryPrice
}

export type EmbroideryTypeSneakPeeksSummary = {
  type: EmbroideryTypeKey
  lines: ReadonlyArray<EmbroideryLine>
  subtotal: EmbroideryPrice
}

export type EmbroideryGroupSummary =
  | {
      layout: 'byType'
      total: EmbroideryPrice
      sections: Array<EmbroideryTypeSneakPeeksSummary>
      unrecognizedLines: ReadonlyArray<EmbroideryLine>
    }
  | {
      layout: 'combined'
      total: EmbroideryPrice
      lines: ReadonlyArray<EmbroideryLine>
    }

export function summarizeEmbroideryGroup(
  lines: ReadonlyArray<EmbroideryLine>
): EmbroideryGroupSummary {
  const allV2Emb =
    lines.length > 0 &&
    lines.every(line => line.properties._embroidery_version === EMBROIDERY_VERSION_V2)

  if (allV2Emb) {
    // group items by `_type` for display
    const sections = EMBROIDERY_TYPES.map(type => {
      const typeLines = lines.filter(l => l.properties._type === type)
      return { type, lines: typeLines, subtotal: sumPrices(typeLines) }
    }).filter(s => s.lines.length > 0)

    // lines without a recognized `_type` are collected for reporting by the caller
    const unrecognizedLines = lines.filter(
      l => !(EMBROIDERY_TYPES as readonly string[]).includes(l.properties._type ?? '')
    )

    // supports grouped display for embroidery multiplacement
    return {
      layout: 'byType',
      sections,
      unrecognizedLines,
      total: sumPrices(lines),
    }
  }

  // supports legacy display
  return {
    layout: 'combined',
    lines,
    total: sumPrices(lines),
  }
}

function sumPrices(lines: ReadonlyArray<{ price: EmbroideryPrice }>): EmbroideryPrice {
  return lines.reduce(
    (acc, { price }) => {
      acc.effectivePrice += price.effectivePrice
      acc.fullPrice += price.fullPrice
      return acc
    },
    { effectivePrice: 0, fullPrice: 0 }
  )
}
